import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { matchPath, useHistory, useLocation, useParams } from "react-router-dom"
import {
  PATHS_WITHOUT_NAV_SKIP_LINK,
  SKIP_LINKS_SELECTORS
} from "../../../../../mp-core/components/SkipLinks"
import { AssessmentSet, LicensesSet, User } from "../../../../../mp-database"
import { MainNavLinksType, ProgramsContext } from ".."
import getMainNavLinks from "../../../../../mp-core/helpers/navLinks/getMainNavLinks"

export const useSkipLinks = (
  productsDropdownRef: React.MutableRefObject<HTMLDivElement>,
  programsDropdownRef: React.MutableRefObject<HTMLDivElement>,
  user: User
) => {
  const [skipLinks, setSkipLinks] = useState<[string, HTMLElement][]>([])

  const location = useLocation()

  useEffect(() => {
    const skipLinksSetter = () => {
      const productsDropdownButton =
        productsDropdownRef.current?.querySelector<HTMLElement>("button")
      const programsDropdownButton =
        programsDropdownRef.current?.querySelector<HTMLElement>("button")

      const isProductsDropdownDisabled =
        productsDropdownButton?.getAttribute("disabled") !== null ||
        productsDropdownButton?.getAttribute("tabindex") === "-1"
      const isProgramsDropdownDisabled =
        programsDropdownButton?.getAttribute("disabled") !== null ||
        programsDropdownButton?.getAttribute("tabindex") === "-1"

      const navigationDropdownsElement =
        !!productsDropdownButton && !isProductsDropdownDisabled
          ? productsDropdownButton
          : !isProgramsDropdownDisabled
          ? programsDropdownButton
          : null

      const navigationElement = !!navigationDropdownsElement
        ? navigationDropdownsElement
        : document.getElementById(
            SKIP_LINKS_SELECTORS.navigationTopBar.value
          ) ||
          document.getElementById(
            SKIP_LINKS_SELECTORS.navigationSubNavBar.value
          )

      const appFiltersElement = document.getElementById(
        SKIP_LINKS_SELECTORS.appFilters.value
      )

      const mainContentElement = document.querySelector<HTMLElement>(
        SKIP_LINKS_SELECTORS.mainContent.value
      )

      setSkipLinks([
        [
          "navigation",
          !PATHS_WITHOUT_NAV_SKIP_LINK.find(path =>
            matchPath(location.pathname, { path })
          )
            ? navigationElement
            : null
        ],
        ["appFilters", user.role !== "Student" && appFiltersElement],
        ["mainContent", mainContentElement]
      ])
    }

    const timeoutId = setTimeout(() => {
      skipLinksSetter()
    }, 700)

    return () => clearTimeout(timeoutId)
  }, [location.pathname])

  return {
    skipLinks
  }
}

export const useCheckUsersCurrentPageAccess = (
  user: User,
  userLicenses: LicensesSet[],
  assessmentSets: { [key: string]: AssessmentSet }
) => {
  const userRef = useRef<User>(null)

  const params = useParams()
  const history = useHistory()
  const location = useLocation()

  const {
    licensedPrograms = [],
    currentProgramCourse = [],
    programsAndCoursesLoading = false,
    checkProgramType
  } = useContext(ProgramsContext) || {}

  const currentExistingProgram = currentProgramCourse?.program?.toLowerCase()

  const links = !!user
    ? (getMainNavLinks({
        user,
        licenses: userLicenses ?? [],
        location,
        assessmentSets,
        params,
        currentProgram: currentExistingProgram,
        checkProgramType
      }) as MainNavLinksType)
    : null

  useEffect(() => {
    if (programsAndCoursesLoading) {
      userRef.current = null
    }

    if (!user || !links || !currentExistingProgram || programsAndCoursesLoading)
      return

    if (!!userRef.current) return

    userRef.current = user

    const match = matchPath<{ program: string; page: string }>(
      location.pathname,
      {
        path: [
          "/eoc/:program",
          "/ebooks/:program",
          "/snap/teacher/elements/:program",
          "/:page/:module/:program"
        ]
      }
    )

    const urlProgram = match?.params?.program || ""

    if (
      !urlProgram ||
      !licensedPrograms.includes(urlProgram) ||
      urlProgram === currentExistingProgram
    )
      return

    const replaceUrl = links?.products?.find(item =>
      item.url.includes(currentExistingProgram)
    )?.url

    if (!replaceUrl) return

    history.replace(replaceUrl)
  }, [
    user,
    licensedPrograms,
    currentExistingProgram,
    programsAndCoursesLoading
  ])
}
